import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, Plus } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorNote,
  Field,
  IdChip,
  Input,
  Pagination,
  Skeleton,
  type Column,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useT } from '../../lib/i18n';
import { errorMessage, useToast } from '../../lib/toast';
import type { BlacklistEntry, Paginated } from '../../lib/types';

/**
 * The opt-out list.
 *
 * Checked on every send path — broadcasts and the case queue alike — because
 * "stop messaging me" has to mean all of them. Removing someone lets them
 * receive again; it is not an undo of the record that they asked.
 */
export function BlacklistTab() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<BlacklistEntry | null>(null);
  const [deviceId, setDeviceId] = useState('');

  const entries = useQuery({
    queryKey: ['blacklist', page],
    queryFn: () =>
      api.get<Paginated<BlacklistEntry>>('/api/notifications/blacklist', {
        page,
        limit: 50,
      }),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['blacklist'] });
    void queryClient.invalidateQueries({ queryKey: ['blacklist-count'] });
  };

  const add = useMutation({
    mutationFn: (reason: string) =>
      api.post('/api/notifications/blacklist', {
        deviceId: deviceId.trim(),
        reason,
      }),
    onSuccess: () => {
      toast.success(t('notif.blacklist.added'));
      setAdding(false);
      setDeviceId('');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (reason: string) =>
      api.delete(`/api/notifications/blacklist/${removing!.userId}`, { reason }),
    onSuccess: () => {
      toast.success(t('notif.blacklist.removed'));
      setRemoving(null);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const columns: Column<BlacklistEntry>[] = [
    {
      key: 'device',
      header: t('notif.blacklist.column.device'),
      render: (entry) => (
        <span className="font-mono text-xs break-anywhere">
          {entry.deviceId}
        </span>
      ),
    },
    {
      key: 'user',
      header: t('notif.blacklist.column.user'),
      desktopOnly: true,
      render: (entry) => <IdChip id={entry.userId} />,
    },
    {
      key: 'reason',
      header: t('notif.blacklist.column.why'),
      render: (entry) => (
        <span className="text-xs text-ink-2">{entry.reason ?? '—'}</span>
      ),
    },
    {
      key: 'actor',
      header: t('notif.blacklist.column.addedBy'),
      desktopOnly: true,
      render: (entry) => (
        <span className="text-ink-2">
          {entry.actor} · {formatDateTime(entry.createdAt)}
        </span>
      ),
    },
    {
      key: 'action',
      header: '',
      numeric: true,
      render: (entry) => (
        <Button className="text-xs" onClick={() => setRemoving(entry)}>
          {t('notif.blacklist.unsilence')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card>
        <CardHeader
          title={t('notif.blacklist.title')}
          description={t('notif.blacklist.description')}
          action={
            <Button
              variant="primary"
              icon={<Plus size={15} />}
              onClick={() => setAdding(true)}
            >
              {t('notif.blacklist.add')}
            </Button>
          }
        />

        {entries.isError && <ErrorNote error={entries.error} />}

        {entries.isPending ? (
          <Skeleton className="h-64" />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={entries.data?.items ?? []}
              rowKey={(entry) => entry.id}
              empty={
                <EmptyState
                  icon={<BellOff size={28} />}
                  title={t('notif.blacklist.emptyTitle')}
                  description={t('notif.blacklist.emptyDescription')}
                />
              }
            />
            <Pagination
              page={entries.data?.page ?? 1}
              totalPages={entries.data?.totalPages ?? 1}
              total={entries.data?.total ?? 0}
              onChange={setPage}
            />
          </>
        )}
      </Card>

      <ConfirmDialog
        open={adding}
        title={t('notif.blacklist.addTitle')}
        description={t('notif.blacklist.addDescription')}
        confirmLabel={t('notif.blacklist.addAction')}
        loading={add.isPending}
        onCancel={() => setAdding(false)}
        onConfirm={(reason) => add.mutate(reason)}
      >
        <Field label={t('common.deviceId')}>
          <Input
            className="font-mono text-xs"
            value={deviceId}
            onChange={(event) => setDeviceId(event.target.value)}
            placeholder={t('common.pasteDeviceId')}
          />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={removing !== null}
        title={t('notif.blacklist.removeTitle')}
        description={t('notif.blacklist.removeDescription')}
        confirmLabel={t('notif.blacklist.unsilence')}
        loading={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={(reason) => remove.mutate(reason)}
      />
    </>
  );
}
